import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

export type SkeletonType =
  | 'table'
  | 'cards'
  | 'dashboard'
  | 'form'
  | 'kanban'
  | 'profile'
  | 'org-chart'
  | 'payslips'
  | 'calendar';

@Component({
  selector: 'app-skeleton-loader',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="sk-wrap" *ngIf="type === 'table'">
      <div class="sk-toolbar">
        <div class="sk-block sk-pulse" style="width:220px;height:36px;border-radius:8px"></div>
        <div class="sk-block sk-pulse" style="width:140px;height:36px;border-radius:8px"></div>
        <div style="flex:1"></div>
        <div class="sk-block sk-pulse" style="width:100px;height:36px;border-radius:8px"></div>
        <div class="sk-block sk-pulse" style="width:110px;height:36px;border-radius:8px"></div>
      </div>
      <div class="sk-table-wrap">
        <div class="sk-thead">
          <div class="sk-th sk-pulse" *ngFor="let c of cols"></div>
        </div>
        <div class="sk-tbody">
          <div class="sk-row" *ngFor="let r of rows">
            <div class="sk-cell-av">
              <div class="sk-av sk-pulse"></div>
              <div class="sk-av-lines">
                <div class="sk-block sk-pulse" style="width:100px;height:13px"></div>
                <div class="sk-block sk-pulse" style="width:70px;height:11px;margin-top:5px"></div>
              </div>
            </div>
            <div class="sk-block sk-pulse sk-cell" *ngFor="let c of cols.slice(1)" [style.width]="c + 'px'"></div>
          </div>
        </div>
      </div>
      <div class="sk-pagination">
        <div class="sk-block sk-pulse" style="width:120px;height:13px"></div>
        <div class="sk-block sk-pulse" style="width:160px;height:32px;border-radius:8px"></div>
      </div>
    </div>

    <div class="sk-wrap" *ngIf="type === 'cards'">
      <div class="sk-toolbar">
        <div class="sk-block sk-pulse" style="width:200px;height:36px;border-radius:8px"></div>
        <div style="flex:1"></div>
        <div class="sk-block sk-pulse" style="width:110px;height:36px;border-radius:8px"></div>
      </div>
      <div class="sk-card-grid">
        <div class="sk-card-item" *ngFor="let c of cardItems">
          <div class="sk-card-thumb sk-pulse"></div>
          <div class="sk-card-body">
            <div class="sk-block sk-pulse" style="width:70%;height:15px"></div>
            <div class="sk-block sk-pulse" style="width:90%;height:11px;margin-top:8px"></div>
            <div class="sk-block sk-pulse" style="width:65%;height:11px;margin-top:5px"></div>
            <div class="sk-card-foot-row">
              <div class="sk-block sk-pulse" style="width:60px;height:22px;border-radius:20px"></div>
              <div class="sk-block sk-pulse" style="width:40px;height:22px;border-radius:20px"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="sk-wrap" *ngIf="type === 'dashboard'">
      <div class="sk-stats-row">
        <div class="sk-stat-card sk-pulse" *ngFor="let s of [1,2,3,4,5]"></div>
      </div>
      <div class="sk-dash-grid">
        <div class="sk-dash-chart sk-pulse"></div>
        <div class="sk-dash-chart sk-pulse" style="height:200px"></div>
        <div class="sk-dash-chart sk-pulse" style="height:220px"></div>
        <div class="sk-dash-chart sk-pulse" style="height:200px"></div>
      </div>
      <div class="sk-dash-bottom">
        <div class="sk-dash-table sk-pulse"></div>
        <div class="sk-dash-mini sk-pulse"></div>
      </div>
    </div>

    <div class="sk-wrap" *ngIf="type === 'form'">
      <div class="sk-form-header">
        <div class="sk-block sk-pulse" style="width:180px;height:20px"></div>
        <div class="sk-block sk-pulse" style="width:280px;height:13px;margin-top:8px"></div>
      </div>
      <div class="sk-form-body">
        <div class="sk-form-row" *ngFor="let r of formRows">
          <div class="sk-form-field" *ngFor="let f of r">
            <div class="sk-block sk-pulse" style="height:12px;width:90px;margin-bottom:8px"></div>
            <div class="sk-block sk-pulse" style="height:40px;border-radius:8px"></div>
          </div>
        </div>
        <div class="sk-block sk-pulse" style="height:80px;border-radius:8px;margin-top:8px"></div>
        <div class="sk-block sk-pulse" style="width:120px;height:38px;border-radius:8px;margin-top:16px"></div>
      </div>
    </div>

    <div class="sk-wrap" *ngIf="type === 'kanban'">
      <div class="sk-k-hero">
        <div class="sk-av sk-pulse" style="width:64px;height:64px;border-radius:12px"></div>
        <div style="display:flex;flex-direction:column;gap:8px">
          <div class="sk-block sk-pulse" style="width:130px;height:17px"></div>
          <div class="sk-block sk-pulse" style="width:90px;height:12px"></div>
        </div>
        <div class="sk-k-stats">
          <div class="sk-stat-card sk-pulse" *ngFor="let s of [1,2,3,4]" style="height:64px;width:90px"></div>
        </div>
      </div>
      <div class="sk-k-board">
        <div class="sk-k-col" *ngFor="let col of kanbanCols; let i = index" [style.animation-delay]="(i*0.08)+'s'">
          <div class="sk-k-col-head">
            <div class="sk-block sk-pulse" style="width:28px;height:28px;border-radius:8px"></div>
            <div class="sk-block sk-pulse" style="flex:1;height:13px"></div>
            <div class="sk-block sk-pulse" style="width:26px;height:22px;border-radius:20px"></div>
          </div>
          <div class="sk-k-col-body">
            <div class="sk-k-card" *ngFor="let c of col">
              <div class="sk-k-accent" [style.background]="kanbanColors[i]"></div>
              <div class="sk-k-inner">
                <div class="sk-block sk-pulse" style="width:90px;height:20px;border-radius:20px"></div>
                <div class="sk-block sk-pulse" style="height:13px;width:88%"></div>
                <div class="sk-block sk-pulse" style="height:11px;width:65%"></div>
                <div class="sk-k-foot">
                  <div class="sk-block sk-pulse" style="width:55px;height:11px"></div>
                  <div class="sk-block sk-pulse" style="width:52px;height:24px;border-radius:7px"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="sk-wrap" *ngIf="type === 'profile'">
      <div class="sk-profile-hero">
        <div class="sk-av sk-pulse" style="width:90px;height:90px;border-radius:50%"></div>
        <div class="sk-profile-info">
          <div class="sk-block sk-pulse" style="width:160px;height:20px"></div>
          <div class="sk-block sk-pulse" style="width:110px;height:13px;margin-top:8px"></div>
          <div class="sk-block sk-pulse" style="width:130px;height:13px;margin-top:6px"></div>
        </div>
      </div>
      <div class="sk-tabs-row">
        <div class="sk-tab sk-pulse" *ngFor="let t of [1,2,3,4]"></div>
      </div>
      <div class="sk-form-body">
        <div class="sk-form-row" *ngFor="let r of formRows">
          <div class="sk-form-field" *ngFor="let f of r">
            <div class="sk-block sk-pulse" style="height:12px;width:90px;margin-bottom:8px"></div>
            <div class="sk-block sk-pulse" style="height:40px;border-radius:8px"></div>
          </div>
        </div>
      </div>
    </div>

    <div class="sk-wrap" *ngIf="type === 'org-chart'">
      <div class="sk-toolbar">
        <div class="sk-block sk-pulse" style="width:200px;height:36px;border-radius:8px"></div>
      </div>
      <div class="sk-org-tree">
        <div class="sk-org-root"><div class="sk-org-node sk-pulse"></div></div>
        <div class="sk-org-level">
          <div class="sk-org-node sk-pulse" *ngFor="let n of [1,2,3]"></div>
        </div>
        <div class="sk-org-level">
          <div class="sk-org-node sk-pulse" *ngFor="let n of [1,2,3,4,5]" style="width:110px;height:55px"></div>
        </div>
      </div>
    </div>

    <div class="sk-wrap" *ngIf="type === 'payslips'">
      <div class="sk-stats-row" style="margin-bottom:20px">
        <div class="sk-stat-card sk-pulse" *ngFor="let s of [1,2,3]"></div>
      </div>
      <div class="sk-card-grid" style="grid-template-columns:repeat(3,1fr)">
        <div class="sk-card-item" *ngFor="let c of [1,2,3,4,5,6]">
          <div class="sk-card-thumb sk-pulse" style="height:60px"></div>
          <div class="sk-card-body">
            <div class="sk-block sk-pulse" style="width:80%;height:15px"></div>
            <div class="sk-block sk-pulse" style="width:60%;height:12px;margin-top:8px"></div>
            <div class="sk-block sk-pulse" style="width:100px;height:32px;border-radius:8px;margin-top:12px"></div>
          </div>
        </div>
      </div>
    </div>

    <div class="sk-wrap" *ngIf="type === 'calendar'">
      <div class="sk-toolbar">
        <div class="sk-block sk-pulse" style="width:36px;height:36px;border-radius:8px"></div>
        <div class="sk-block sk-pulse" style="width:160px;height:20px"></div>
        <div class="sk-block sk-pulse" style="width:36px;height:36px;border-radius:8px"></div>
        <div style="flex:1"></div>
        <div class="sk-block sk-pulse" style="width:120px;height:36px;border-radius:8px"></div>
      </div>
      <div class="sk-cal-grid">
        <div class="sk-cal-day-head sk-pulse" *ngFor="let d of [1,2,3,4,5,6,7]"></div>
        <div class="sk-cal-cell sk-pulse" *ngFor="let c of calCells"></div>
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; width: 100%; }

    @keyframes shimmer {
      0%   { background-position: -600px 0; }
      100% { background-position:  600px 0; }
    }
    @keyframes fadeIn {
      from { opacity: 0; transform: translateY(4px); }
      to   { opacity: 1; transform: translateY(0); }
    }

    .sk-pulse {
      background: linear-gradient(90deg, #ece9e6 0%, #f7f5f3 40%, #ece9e6 80%);
      background-size: 600px 100%;
      animation: shimmer 1.5s infinite linear;
      border-radius: 6px;
    }

    .sk-wrap {
      padding: 20px 24px;
      display: flex; flex-direction: column; gap: 16px;
      animation: fadeIn .25s ease;
    }

    .sk-block { display: block; }

    .sk-toolbar {
      display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
    }

    .sk-table-wrap {
      background: #fff; border-radius: 12px;
      border: 1px solid #e8e5e2; overflow: hidden;
    }
    .sk-thead {
      display: grid; grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
      gap: 12px; padding: 12px 18px; background: #f7f5f3;
      border-bottom: 1px solid #e8e5e2;
    }
    .sk-th { height: 12px; }
    .sk-tbody { display: flex; flex-direction: column; }
    .sk-row {
      display: grid; grid-template-columns: 2fr 1fr 1fr 1fr 1fr;
      gap: 12px; padding: 14px 18px; align-items: center;
      border-bottom: 1px solid #f0eeec;
      &:last-child { border-bottom: none; }
    }
    .sk-cell-av { display: flex; align-items: center; gap: 10px; }
    .sk-av { width: 38px; height: 38px; border-radius: 10px; flex-shrink: 0; }
    .sk-av-lines { display: flex; flex-direction: column; }
    .sk-cell { height: 13px; }
    .sk-pagination {
      display: flex; justify-content: space-between; align-items: center;
      padding: 12px 18px; background: #fff; border: 1px solid #e8e5e2;
      border-top: none; border-radius: 0 0 12px 12px;
    }

    .sk-card-grid {
      display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
      gap: 16px;
    }
    .sk-card-item {
      background: #fff; border-radius: 12px; border: 1px solid #e8e5e2;
      overflow: hidden; animation: fadeIn .3s ease both;
    }
    .sk-card-thumb { height: 120px; border-radius: 0; width: 100%; }
    .sk-card-body { padding: 14px 16px; display: flex; flex-direction: column; gap: 0; }
    .sk-card-foot-row {
      display: flex; align-items: center; gap: 8px; margin-top: 14px;
    }

    .sk-stats-row {
      display: flex; gap: 12px; flex-wrap: wrap;
    }
    .sk-stat-card {
      flex: 1; min-width: 140px; height: 88px; border-radius: 12px;
    }
    .sk-dash-grid {
      display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
    }
    .sk-dash-chart { height: 240px; border-radius: 12px; }
    .sk-dash-bottom { display: grid; grid-template-columns: 2fr 1fr; gap: 16px; }
    .sk-dash-table { height: 200px; border-radius: 12px; }
    .sk-dash-mini   { height: 200px; border-radius: 12px; }

    .sk-form-header { padding-bottom: 4px; }
    .sk-form-body {
      background: #fff; border-radius: 12px; border: 1px solid #e8e5e2;
      padding: 22px 24px; display: flex; flex-direction: column; gap: 20px;
    }
    .sk-form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
    .sk-form-field { display: flex; flex-direction: column; }

    .sk-k-hero {
      display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
      background: #fff; border-radius: 14px; border: 1px solid #e8e5e2;
      padding: 20px 22px; margin-bottom: 4px;
    }
    .sk-k-stats { display: flex; gap: 10px; flex: 1; }
    .sk-k-board {
      display: grid; grid-template-columns: repeat(4, minmax(200px,1fr));
      gap: 14px;
    }
    .sk-k-col {
      border-radius: 14px; background: #f5f4f2;
      border: 1.5px solid #e8e5e2; overflow: hidden;
      animation: fadeIn .3s ease both;
    }
    .sk-k-col-head {
      display: flex; align-items: center; gap: 8px;
      padding: 12px 14px; background: #fff; border-bottom: 1.5px solid #e8e5e2;
    }
    .sk-k-col-body { padding: 10px; display: flex; flex-direction: column; gap: 8px; }
    .sk-k-card {
      display: flex; border-radius: 10px; overflow: hidden;
      border: 1.5px solid #e8e5e2; background: #fff;
    }
    .sk-k-accent { width: 3px; flex-shrink: 0; opacity: .4; }
    .sk-k-inner { flex: 1; padding: 10px 12px; display: flex; flex-direction: column; gap: 7px; }
    .sk-k-foot {
      display: flex; justify-content: space-between; align-items: center;
      padding-top: 7px; border-top: 1px solid #ece9e6;
    }

    .sk-profile-hero {
      display: flex; align-items: center; gap: 20px;
      background: #fff; border-radius: 14px; border: 1px solid #e8e5e2;
      padding: 24px; margin-bottom: 4px;
    }
    .sk-profile-info { display: flex; flex-direction: column; }
    .sk-tabs-row { display: flex; gap: 4px; }
    .sk-tab { width: 100px; height: 36px; border-radius: 8px; }

    .sk-org-tree {
      display: flex; flex-direction: column; align-items: center; gap: 28px;
      padding: 24px;
    }
    .sk-org-root { display: flex; justify-content: center; }
    .sk-org-node {
      width: 140px; height: 70px; border-radius: 12px;
      margin: 0 8px;
    }
    .sk-org-level { display: flex; justify-content: center; gap: 16px; }

    .sk-cal-grid {
      display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px;
      background: #fff; border-radius: 12px; border: 1px solid #e8e5e2;
      padding: 12px;
    }
    .sk-cal-day-head { height: 32px; border-radius: 6px; }
    .sk-cal-cell { height: 80px; border-radius: 8px; }
  `]
})
export class SkeletonLoaderComponent {
  @Input() type: SkeletonType = 'table';
  @Input() rowCount = 8;
  @Input() cardCount = 9;

  get rows() { return Array(this.rowCount).fill(0); }
  get cols() { return [220, 120, 100, 100, 80]; }
  get cardItems() { return Array(this.cardCount).fill(0); }
  get formRows() { return [[1,2],[1,2],[1,2]]; }
  get kanbanCols() { return [[1,2,3,4],[1,2],[1],[1,2,3]]; }
  get kanbanColors() { return ['#94a3b8','#f59e0b','#7c3aed','#22c55e']; }
  get calCells() { return Array(35).fill(0); }
}
